import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  LayoutDashboard,
  MapPin,
  Users,
  FileText,
  LogOut,
  Plus,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// For Emergent deployments, frontend and backend share the same domain
const API_URL = window.location.origin;

// Admin Sidebar
const AdminSidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'centers', label: 'Panchkarma Centers', icon: MapPin },
  ];

  return (
    <div className="admin-sidebar">
      <div className="flex items-center gap-3 mb-8 px-4">
        <img 
          src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
          alt="Logo" 
          className="h-8 w-auto"
        />
        <span className="font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Admin
        </span>
      </div>

      <nav className="space-y-2">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`admin-nav-item w-full ${activeTab === item.id ? 'active' : ''}`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="absolute bottom-4 left-4 right-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="w-full justify-start text-stone-600 mb-2"
        >
          <FileText className="w-5 h-5 mr-3" />
          Back to App
        </Button>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-stone-600 hover:text-red-600"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
};

// Dashboard Stats
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading stats...</div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-[hsl(120,10%,18%)] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
        Dashboard Overview
      </h1>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="stat-card">
          <p className="text-sm text-stone-500 mb-1">Total Users</p>
          <p className="text-3xl font-bold text-[hsl(120,10%,18%)]">{stats?.total_users || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-stone-500 mb-1">Total Consultations</p>
          <p className="text-3xl font-bold text-[hsl(22,70%,44%)]">{stats?.total_consultations || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-stone-500 mb-1">Paid Consultations</p>
          <p className="text-3xl font-bold text-emerald-600">{stats?.paid_consultations || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-stone-500 mb-1">Active Centers</p>
          <p className="text-3xl font-bold text-blue-600">{stats?.total_centers || 0}</p>
        </div>
      </div>
    </div>
  );
};

// Panchkarma Centers Management
const CentersManagement = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    address: '',
    description: '',
    treatments: '',
    image_url: '',
    price_range: '',
    contact_phone: '',
    contact_email: ''
  });

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/panchkarma-centers`, {
        withCredentials: true
      });
      setCenters(response.data);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error('Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      address: '',
      description: '',
      treatments: '',
      image_url: '',
      price_range: '',
      contact_phone: '',
      contact_email: ''
    });
    setEditingCenter(null);
  };

  const handleOpenDialog = (center = null) => {
    if (center) {
      setEditingCenter(center);
      setFormData({
        name: center.name,
        location: center.location,
        address: center.address,
        description: center.description,
        treatments: center.treatments.join(', '),
        image_url: center.image_url || '',
        price_range: center.price_range,
        contact_phone: center.contact_phone || '',
        contact_email: center.contact_email || ''
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      treatments: formData.treatments.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      if (editingCenter) {
        await axios.put(
          `${API_URL}/api/admin/panchkarma-centers/${editingCenter.center_id}`,
          data,
          { withCredentials: true }
        );
        toast.success('Center updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/panchkarma-centers`,
          data,
          { withCredentials: true }
        );
        toast.success('Center created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCenters();
    } catch (error) {
      console.error('Error saving center:', error);
      toast.error('Failed to save center');
    }
  };

  const handleDelete = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this center?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/panchkarma-centers/${centerId}`,
        { withCredentials: true }
      );
      toast.success('Center deleted');
      fetchCenters();
    } catch (error) {
      console.error('Error deleting center:', error);
      toast.error('Failed to delete center');
    }
  };

  return (
    <div data-testid="centers-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Panchkarma Centers
        </h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()}
              className="btn-primary"
              data-testid="add-center-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Center
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingCenter ? 'Edit Center' : 'Add New Center'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Center Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="center-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">City/Location *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    data-testid="center-location-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Full Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="center-address-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="center-description-input"
                />
              </div>
              <div>
                <Label htmlFor="treatments">Treatments (comma-separated) *</Label>
                <Input
                  id="treatments"
                  value={formData.treatments}
                  onChange={(e) => setFormData({ ...formData, treatments: e.target.value })}
                  placeholder="e.g., Abhyanga, Shirodhara, Panchakarma"
                  required
                  data-testid="center-treatments-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price_range">Price Range *</Label>
                  <Input
                    id="price_range"
                    value={formData.price_range}
                    onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
                    placeholder="e.g., $500 - $2000"
                    required
                    data-testid="center-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="center-image-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    data-testid="center-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    data-testid="center-email-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-center-btn">
                  {editingCenter ? 'Update Center' : 'Add Center'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="animate-pulse">Loading centers...</div>
      ) : centers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-600">No centers added yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Treatments</th>
                <th>Price Range</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {centers.map(center => (
                <tr key={center.center_id} data-testid={`center-row-${center.center_id}`}>
                  <td className="font-medium">{center.name}</td>
                  <td>{center.location}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {center.treatments.slice(0, 2).map((t, i) => (
                        <span key={i} className="text-xs bg-stone-100 px-2 py-0.5 rounded">
                          {t}
                        </span>
                      ))}
                      {center.treatments.length > 2 && (
                        <span className="text-xs text-stone-500">+{center.treatments.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td>{center.price_range}</td>
                  <td>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      center.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {center.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(center)}
                        data-testid={`edit-center-${center.center_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(center.center_id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-center-${center.center_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main Admin Panel
export const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)] flex" data-testid="admin-panel">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-1 p-8">
        {activeTab === 'dashboard' && <AdminDashboard />}
        {activeTab === 'centers' && <CentersManagement />}
      </main>
    </div>
  );
};
